import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight, UtensilsCrossed } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"];
const MEALS = ["Desayuno", "Almuerzo", "Merienda"];
const MEAL_STYLES = {
  Desayuno: "bg-amber-50 border-amber-200",
  Almuerzo: "bg-emerald-50 border-emerald-200",
  Merienda: "bg-sky-50 border-sky-200",
};

const mondayOf = (d) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const iso = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const EMPTY = { meal_type: "Almuerzo", dish: "", notes: "" };

export default function Menus() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [weekStart, setWeekStart] = useState(iso(mondayOf(new Date())));
  const [dialog, setDialog] = useState(null); // {date, day}
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const canWrite = ["admin", "secretaria"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/r/menus").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const weekEnd = iso(addDays(new Date(weekStart + "T00:00:00"), 4));
  const weekItems = items.filter((m) => m.date >= weekStart && m.date <= weekEnd);

  const shiftWeek = (n) => setWeekStart(iso(addDays(new Date(weekStart + "T00:00:00"), n * 7)));

  const openCreate = (date, day) => {
    setEditing(null);
    setForm(EMPTY);
    setDialog({ date, day });
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({ meal_type: m.meal_type, dish: m.dish, notes: m.notes || "" });
    setDialog({ date: m.date, day: m.day });
  };

  const submit = async () => {
    if (!form.dish.trim()) { toast.error("Escribe el plato"); return; }
    try {
      if (editing) {
        await api.put(`/r/menus/${editing.id}`, form);
        toast.success("Menú actualizado");
      } else {
        await api.post("/r/menus", { ...form, date: dialog.date, day: dialog.day });
        toast.success("Plato agregado al menú");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/r/menus/${m.id}`);
      toast.success("Plato eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fmtDay = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("es-DO", { day: "numeric", month: "short" });
  };

  return (
    <div data-testid="menus-page">
      <PageHeader title="Menú del comedor" subtitle="Organizador semanal de desayunos, almuerzos y meriendas">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="week-prev" onClick={() => shiftWeek(-1)}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium text-slate-700 min-w-40 text-center" data-testid="week-label">
            {fmtDay(weekStart)} — {fmtDay(weekEnd)}
          </span>
          <Button variant="outline" size="sm" data-testid="week-next" onClick={() => shiftWeek(1)}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" data-testid="week-today" onClick={() => setWeekStart(iso(mondayOf(new Date())))}>Hoy</Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="menu-week-board">
        {DAYS.map((day, i) => {
          const date = iso(addDays(new Date(weekStart + "T00:00:00"), i));
          const dayMeals = weekItems.filter((m) => m.date === date)
            .sort((a, b) => MEALS.indexOf(a.meal_type) - MEALS.indexOf(b.meal_type));
          return (
            <div key={day} className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col" data-testid={`menu-day-${day}`}>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{day}</p>
                  <p className="text-xs text-slate-400">{fmtDay(date)}</p>
                </div>
                {canWrite && (
                  <button data-testid={`menu-add-${day}`} onClick={() => openCreate(date, day)}
                    className="w-7 h-7 rounded-lg bg-blue-50 text-blue-800 hover:bg-blue-100 flex items-center justify-center transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="p-3 space-y-2 flex-1">
                {dayMeals.map((m) => (
                  <div key={m.id} className={`border rounded-lg p-2.5 text-xs ${MEAL_STYLES[m.meal_type] || "bg-slate-50 border-slate-200"}`} data-testid={`menu-item-${m.id}`}>
                    <div className="flex items-start justify-between gap-1">
                      <span className="font-semibold text-slate-500 uppercase tracking-wide text-[10px]">{m.meal_type}</span>
                      {canWrite && (
                        <div className="flex gap-0.5">
                          <button data-testid={`menu-edit-${m.id}`} onClick={() => openEdit(m)} className="text-slate-400 hover:text-blue-800"><UtensilsCrossed className="w-3 h-3" /></button>
                          <button data-testid={`menu-delete-${m.id}`} onClick={() => remove(m)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      )}
                    </div>
                    <p className="text-slate-800 font-medium mt-0.5">{m.dish}</p>
                    {m.notes && <p className="text-slate-500 mt-0.5">{m.notes}</p>}
                  </div>
                ))}
                {dayMeals.length === 0 && <p className="text-xs text-slate-300 text-center py-4">Sin menú</p>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="menu-item-modal">
          <DialogHeader><DialogTitle>{editing ? "Editar plato" : "Agregar plato"} — {dialog?.day} {dialog && fmtDay(dialog.date)}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.meal_type} onValueChange={(v) => setForm({ ...form, meal_type: v })}>
                <SelectTrigger data-testid="menu-meal-type"><SelectValue /></SelectTrigger>
                <SelectContent>{MEALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Plato</Label><Input data-testid="menu-dish" placeholder="Ej: Arroz con pollo, ensalada y jugo" value={form.dish} onChange={(e) => setForm({ ...form, dish: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Notas (opcional)</Label><Input data-testid="menu-notes" placeholder="Ej: opción vegetariana disponible" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="menu-cancel" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button data-testid="menu-save" className="bg-blue-800 hover:bg-blue-700" onClick={submit}>{editing ? "Guardar" : "Agregar"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
